<template>
  <div v-if="getDevTippsEnabled" ref="devTipIcon" @click="toggle"
    class="cursor-pointer flex justify-center items-center relative w-16 h-16 min-w-16 max-w-16
        min-h-16 max-h-16 rounded-full bg-[var(--color-milk)] bg-cover">
    <font-awesome-icon :class="{ 'text-[var(--color-orange)]': hasNew }" class="text-5xl"
        icon="fa-regular fa-lightbulb"/>
    <font-awesome-icon v-if="hasNew" icon="fa-circle"
      class="absolute top-0 left-0 text-xl text-[var(--color-orange)]"/>
  </div>
  <div v-if="isPopoverVisible" ref="popoverContent"
    class="fixed z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-4"
    :style="popoverPosition">
    <h3 class="my-4">{{ yourTipsHeading }}</h3>
    <div class="overflow-y-auto flex-grow max-h-[70vh]">
      <dev-tip-item-component v-for="item in getDevTippOverview"
       :key="item.tip_id || item._id || item" :tip="item" @feedback-submitted="refreshDevTipList"/>
      <div v-if="!getDevTippOverview || getDevTippOverview.length === 0"
           class="text-center py-8 text-gray-500">
        <font-awesome-icon icon="fa-regular fa-lightbulb" class="text-5xl mb-4 text-gray-300"/>
        <p class="text-lg font-medium">{{ noTipsHeading }}</p>
        <p class="text-sm mt-2">{{ noTipsDescription }}</p>
      </div>
    </div>
          <div class="grid place-items-center justify-end mb-1">
        <p>
          <a @click.prevent="openDevTipLibrary" class="cursor-pointer text-[#999999]">
            {{ $t('DevTipItemContainerComponent.showMore') }} >
          </a>
        </p>
      </div>
  </div>
  <DevTipLibraryOverlayComponent :visible="showDialog"
  @update:visible="showDialog = $event"></DevTipLibraryOverlayComponent>
</template>

<script>
import DevTipItemComponent from '@/components/Home/Shared/DevTip/DevTipItemComponent.vue';
import DevTipLibraryOverlayComponent
  from '@/components/Home/Shared/FrontendOverlays/DevTipLibraryOverlayComponent.vue';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';
import { isFeatureVisibleForCustomer, isFeatureLockedForCustomer } from '@/utils/featureAccess';
import { navigateCustomerNavItem } from '@/utils/customerNavClick';

export default {
  name: 'DevTipIconComponent',
  components: {
    DevTipLibraryOverlayComponent,
    DevTipItemComponent,
  },
  data() {
    return {
      showDialog: false,
      isPopoverVisible: false,
      refetchingAfterOpen: false,
    };
  },
  computed: {
    getDevTippsEnabled() {
      return isFeatureVisibleForCustomer(this.$store, 'developmentTip');
    },
    getShowDevTipps() {
      return this.$store.getters.getShowDevTipps;
    },
    hasNew() {
      return this.$store.getters.getHasNewDevTipps;
    },
    getDevTippOverview() {
      return this.$store.getters.getDevTippOverview;
    },
    developmentTipProductName() {
      return getFeatureDisplayNameOrFallback(this.$store, 'developmentTip', this.$t('DevTipItemContainerComponent.developmentTipsFallback'),this.$i18n.locale);
    },
    yourTipsHeading() {
      return this.$t('DevTipItemContainerComponent.yourTipsNamed', {
        name: this.developmentTipProductName,
      });
    },
    noTipsHeading() {
      return this.$t('DevTipItemContainerComponent.noTipsNamed', {
        name: this.developmentTipProductName,
      });
    },
    noTipsDescription() {
      return this.$t('DevTipItemContainerComponent.noTipsDescriptionNamed', {
        name: this.developmentTipProductName,
      });
    },
    popoverPosition() {
      if (!this.$refs.devTipIcon) return {};

      const rect = this.$refs.devTipIcon.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate optimal position
      let left = rect.left + (rect.width / 2);
      let top = rect.bottom + 10;

      // Ensure popover doesn't go off-screen to the right
      if (left + 200 > viewportWidth) {
        left = viewportWidth - 220;
      }

      // Ensure popover doesn't go off-screen to the left
      if (left < 200) {
        left = 220;
      }

      // Ensure popover doesn't go off-screen to the bottom
      if (top + 400 > viewportHeight) {
        top = rect.top - 410; // Show above the icon instead
      }

      return {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        transform: 'translateX(-50%)', // Center the popover on the calculated position
        maxWidth: '400px',
        minWidth: '320px',
        zIndex: 9999,
      };
    },
  },
  methods: {
    toggle() {
      try {
        if (isFeatureLockedForCustomer(this.$store, 'developmentTip')) {
          navigateCustomerNavItem(this.$router, this.$store, {
            itemKind: 'link',
            catalogKey: 'developmentTip',
            openAs: 'overlay',
            locked: true,
          });
          return;
        }
        this.$store.commit('toggleShowDevTipps');

        // Always show popover, even if no DevTips are available
        // This allows users to see the "no tips available" message
        this.isPopoverVisible = !this.isPopoverVisible;

        // Once the bulb is clicked and the popover opens,
        // clear the orange dot by marking new tips as viewed
        if (this.isPopoverVisible) {
          this.refetchingAfterOpen = true;
          const overview = this.$store.getters.getDevTippOverview || [];
          const newTips = overview.filter((el) => el?.new ?? el?.isNew);
          Promise.all(
            newTips.map((tip) => this.$store.dispatch('devTips/markDevTipAsViewed', tip.tip_id || tip._id)),
          )
            .then(() => this.$store.dispatch('getDevTippOverview'))
            .catch(() => {})
            .finally(() => { this.refetchingAfterOpen = false; });
        }
      } catch (error) {
        console.warn('Error toggling DevTip popover:', error);
        this.isPopoverVisible = false;
      }
    },
    openDevTipLibrary() {
      this.showDialog = true;
    },
    refreshDevTipList() {
      // Hide popover and refresh the DevTip list
      this.hidePopover();
      this.$store.dispatch('getDevTippOverview');
    },
    hidePopover() {
      this.isPopoverVisible = false;
    },
    handleClickOutside(event) {
      // Close popover if clicking outside
      if (this.isPopoverVisible &&
        !this.$refs.devTipIcon?.contains(event.target) &&
        !this.$refs.popoverContent?.contains(event.target)) {
        this.hidePopover();
      }
    },
    handleResize() {
      // Force position recalculation on window resize
      if (this.isPopoverVisible) {
        this.$forceUpdate();
      }
    },
  },
  mounted() {
    this.$store.dispatch('getDevTippOverview');

    // Add click outside handler to close popover
    document.addEventListener('click', this.handleClickOutside);

    // Add window resize handler for responsive positioning
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    // Remove click outside handler
    document.removeEventListener('click', this.handleClickOutside);

    // Remove window resize handler
    window.removeEventListener('resize', this.handleResize);
  },
  watch: {
    getShowDevTipps(open) {
      if (open) {
        this.isPopoverVisible = true;
        this.$store.commit('setShowDevTipps', false);
      }
    },
    getDevTippOverview: {
      handler() {
        // If popover is visible and list changes,
        // hide it — unless we just refetched after opening (to clear orange)
        if (this.isPopoverVisible && !this.refetchingAfterOpen) {
          this.hidePopover();
        }
      },
      deep: true,
    },
  },
};
</script>
